import type { FC } from 'react';
import type { Control, UseFormSetValue } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  ArrowDownCircle,
  ArrowUpCircle,
  Ban,
  Bitcoin,
  Landmark,
} from 'lucide-react';

import { cn } from '@/shared/libs';
import { FormField, FormSwitch, FormTagsInput, Label } from '@/shared/ui';
import {
  FIXED_FEE_ASSET,
  formatLegMargin,
  OFFER_LEG_I18N_KEY,
  OFFER_LEG_KEYS,
  type OfferLegKey,
} from '@entities/Offer';

import type { OfferFormValues } from '../libs/offerSchema';

import type { TagOption } from './OfferCoverageSection';

export interface OfferPricingSectionProps {
  control: Control<OfferFormValues>;
  methodOptions: TagOption[];
  setValue: UseFormSetValue<OfferFormValues>;
  values: OfferFormValues;
}

const SETTLEMENT_LEGS = new Set<OfferLegKey>(['crypto_topup', 'settlement']);

const LEG_ICON: Record<OfferLegKey, typeof ArrowDownCircle> = {
  crypto_topup: Bitcoin,
  payin: ArrowDownCircle,
  payout: ArrowUpCircle,
  settlement: Landmark,
};

const fieldClass =
  'w-full min-w-0 bg-transparent px-3 py-2 text-sm tabular-nums focus:outline-none disabled:cursor-not-allowed';

const filledUnit = (value: string): boolean =>
  value.trim() !== '' && Number(value) !== 0;

interface UnitPairProps {
  fixed: string;
  fixedDisabled: boolean;
  percent: string;
  percentDisabled: boolean;
  onFixed: (value: string) => void;
  onPercent: (value: string) => void;
}

const UnitPair: FC<UnitPairProps> = ({
  fixed,
  fixedDisabled,
  onFixed,
  onPercent,
  percent,
  percentDisabled,
}) => {
  const { t } = useTranslation();

  return (
    <div className="grid grid-cols-2 gap-1.5">
      <input
        className={cn(
          'rounded-lg border border-border/60 bg-background px-3 py-2 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-indigo-500/40 disabled:cursor-not-allowed disabled:opacity-40',
        )}
        disabled={percentDisabled}
        inputMode="decimal"
        placeholder={t('pages.admin.offers.fields.percentPlaceholder')}
        value={percent}
        onChange={(event) => onPercent(event.target.value)}
      />
      <div
        className={cn(
          'flex overflow-hidden rounded-lg border border-border/60 bg-background focus-within:ring-2 focus-within:ring-indigo-500/40',
          fixedDisabled && 'opacity-40',
        )}>
        <span className="inline-flex shrink-0 items-center border-r border-border/60 bg-foreground/5 px-2.5 text-[11px] font-semibold text-muted-foreground">
          {FIXED_FEE_ASSET}
        </span>
        <input
          className={fieldClass}
          disabled={fixedDisabled}
          inputMode="decimal"
          placeholder={t('pages.admin.offers.fields.fixedPlaceholder')}
          value={fixed}
          onChange={(event) => onFixed(event.target.value)}
        />
      </div>
    </div>
  );
};

UnitPair.displayName = 'UnitPair';

export const OfferPricingSection: FC<OfferPricingSectionProps> = ({
  control,
  methodOptions,
  setValue,
  values,
}) => {
  const { t } = useTranslation();

  const patchLeg = (
    leg: OfferLegKey,
    field: keyof OfferFormValues['legs']['payin'],
    value: string,
  ): void => {
    setValue(`legs.${leg}.${field}`, value);
  };

  return (
    <div className="space-y-3">
      <p className="text-[11px] text-muted-foreground">
        {t('pages.admin.offers.sections.pricingHint')}
      </p>
      {OFFER_LEG_KEYS.map((leg) => {
        const settlement = SETTLEMENT_LEGS.has(leg);
        const current = values.legs[leg];
        const enabled = current.enabled;
        const Icon = LEG_ICON[leg];
        const marginUnit =
          filledUnit(current.cost_fixed) || filledUnit(current.sell_fixed)
            ? 'fixed'
            : 'percent';
        const margin =
          marginUnit === 'fixed'
            ? Number(current.sell_fixed || 0) - Number(current.cost_fixed || 0)
            : Number(current.sell_pct || 0) - Number(current.cost_pct || 0);
        const costPercentOn = filledUnit(current.cost_pct);
        const costFixedOn = filledUnit(current.cost_fixed);
        const sellPercentOn = filledUnit(current.sell_pct);
        const sellFixedOn = filledUnit(current.sell_fixed);
        const minPercentOn = filledUnit(current.min_fee_pct);
        const minFixedOn = filledUnit(current.min_fee_fixed);

        return (
          <div
            key={leg}
            className={cn(
              'rounded-xl border',
              enabled
                ? 'border-indigo-500/30 bg-card dark:bg-white/[0.02]'
                : 'border-border/40 bg-background/40 opacity-70',
            )}>
            <div className="flex items-center justify-between gap-2 px-3 py-2.5">
              <div className="flex min-w-0 items-center gap-2.5">
                <FormField
                  control={control}
                  name={`legs.${leg}.enabled`}
                  render={({ field }) => (
                    <FormSwitch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  )}
                />
                <Icon
                  className={cn(
                    'h-4 w-4 shrink-0',
                    enabled ? 'text-indigo-500' : 'text-muted-foreground',
                  )}
                />
                <span className="truncate text-sm font-semibold tracking-wide uppercase">
                  {t(OFFER_LEG_I18N_KEY[leg])}
                </span>
                {!enabled && (
                  <span className="inline-flex shrink-0 items-center gap-1 text-[11px] text-muted-foreground">
                    <Ban className="h-3 w-3" />
                    {t('pages.admin.offers.fields.notInOffer')}
                  </span>
                )}
              </div>
              {enabled && (
                <span
                  className={cn(
                    'shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium',
                    margin > 0
                      ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                      : 'bg-foreground/5 text-muted-foreground',
                  )}>
                  {t('pages.admin.offers.fields.margin')}{' '}
                  {formatLegMargin(margin, marginUnit)}
                </span>
              )}
            </div>
            {enabled && (
              <div className="space-y-2.5 border-t border-border/40 px-3 pt-2 pb-3">
                {settlement ? (
                  <div className="space-y-1.5">
                    <Label>{t('pages.admin.offers.fields.terms')}</Label>
                    <input
                      className="w-full rounded-xl border border-border/60 bg-background/60 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/40"
                      placeholder={t(
                        'pages.admin.offers.fields.termsPlaceholder',
                      )}
                      value={current.terms}
                      onChange={(event) =>
                        patchLeg(leg, 'terms', event.target.value)
                      }
                    />
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    <Label>
                      {t('pages.admin.offers.fields.paymentMethods')}
                    </Label>
                    <FormTagsInput
                      options={methodOptions}
                      placeholder={t(
                        'pages.admin.offers.fields.paymentMethodsPlaceholder',
                      )}
                      restrict
                      value={current.methods}
                      onChange={(methods) =>
                        setValue(`legs.${leg}.methods`, methods)
                      }
                    />
                  </div>
                )}
                <div className="grid grid-cols-2 gap-2">
                  <div className="rounded-lg border border-border/40 bg-background/60 p-2.5">
                    <p className="mb-1.5 text-[10px] tracking-wider text-muted-foreground uppercase">
                      {t('pages.admin.offers.fields.costTitle')}
                      <span className="ml-0.5 text-rose-500">*</span>
                    </p>
                    <UnitPair
                      fixed={current.cost_fixed}
                      fixedDisabled={costPercentOn}
                      percent={current.cost_pct}
                      percentDisabled={costFixedOn}
                      onFixed={(value) => {
                        patchLeg(leg, 'cost_fixed', value);
                        if (value.trim() !== '') {
                          patchLeg(leg, 'cost_pct', '');
                        }
                      }}
                      onPercent={(value) => {
                        patchLeg(leg, 'cost_pct', value);
                        if (filledUnit(value)) {
                          patchLeg(leg, 'cost_fixed', '');
                        }
                      }}
                    />
                  </div>
                  <div className="rounded-lg border border-indigo-500/25 bg-indigo-500/5 p-2.5">
                    <p className="mb-1.5 text-[10px] tracking-wider text-indigo-600 uppercase dark:text-indigo-300">
                      {t('pages.admin.offers.fields.sellTitle')}
                      <span className="ml-0.5 text-rose-500">*</span>
                    </p>
                    <UnitPair
                      fixed={current.sell_fixed}
                      fixedDisabled={sellPercentOn}
                      percent={current.sell_pct}
                      percentDisabled={sellFixedOn}
                      onFixed={(value) => {
                        patchLeg(leg, 'sell_fixed', value);
                        if (value.trim() !== '') {
                          patchLeg(leg, 'sell_pct', '');
                        }
                      }}
                      onPercent={(value) => {
                        patchLeg(leg, 'sell_pct', value);
                        if (filledUnit(value)) {
                          patchLeg(leg, 'sell_fixed', '');
                        }
                      }}
                    />
                  </div>
                </div>
                {settlement ? (
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1.5">
                      <Label>
                        {t('pages.admin.offers.fields.settlementProvider')}
                      </Label>
                      <input
                        className="w-full rounded-xl border border-border/60 bg-background/60 px-3.5 py-2.5 text-sm focus:ring-2 focus:ring-indigo-500/40 focus:outline-none"
                        placeholder={t(
                          'pages.admin.offers.fields.settlementProviderPlaceholder',
                        )}
                        value={current.provider}
                        onChange={(event) =>
                          patchLeg(leg, 'provider', event.target.value)
                        }
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label>
                        {t('pages.admin.offers.fields.settlementAsset')}
                      </Label>
                      <input
                        className="w-full rounded-xl border border-border/60 bg-background/60 px-3.5 py-2.5 text-sm focus:ring-2 focus:ring-indigo-500/40 focus:outline-none"
                        placeholder={t(
                          'pages.admin.offers.fields.settlementAssetPlaceholder',
                        )}
                        value={current.asset}
                        onChange={(event) =>
                          patchLeg(leg, 'asset', event.target.value)
                        }
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label>{t('pages.admin.offers.fields.minAmount')}</Label>
                      <input
                        className="w-full rounded-xl border border-border/60 bg-background/60 px-3.5 py-2.5 text-sm focus:ring-2 focus:ring-indigo-500/40 focus:outline-none"
                        placeholder={t(
                          'pages.admin.offers.fields.minAmountPlaceholder',
                        )}
                        value={current.min_amount}
                        onChange={(event) =>
                          patchLeg(leg, 'min_amount', event.target.value)
                        }
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label>{t('pages.admin.offers.fields.limits')}</Label>
                      <input
                        className="w-full rounded-xl border border-border/60 bg-background/60 px-3.5 py-2.5 text-sm focus:ring-2 focus:ring-indigo-500/40 focus:outline-none"
                        placeholder={t(
                          'pages.admin.offers.fields.limitsPlaceholder',
                        )}
                        value={current.limits}
                        onChange={(event) =>
                          patchLeg(leg, 'limits', event.target.value)
                        }
                      />
                    </div>
                    <div className="col-span-2 space-y-1.5">
                      <Label>
                        {t('pages.admin.offers.fields.settlementRate')}
                        <span className="ml-0.5 text-rose-500">*</span>
                      </Label>
                      <input
                        className="w-full rounded-xl border border-border/60 bg-background/60 px-3.5 py-2.5 text-sm focus:ring-2 focus:ring-indigo-500/40 focus:outline-none"
                        placeholder={t(
                          'pages.admin.offers.fields.settlementRatePlaceholder',
                        )}
                        value={current.rate}
                        onChange={(event) =>
                          patchLeg(leg, 'rate', event.target.value)
                        }
                      />
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    <div className="rounded-lg border border-border/40 bg-background/60 p-2.5">
                      <p className="mb-1.5 text-[10px] tracking-wider text-muted-foreground uppercase">
                        {t('pages.admin.offers.fields.minFee')}
                      </p>
                      <UnitPair
                        fixed={current.min_fee_fixed}
                        fixedDisabled={minPercentOn}
                        percent={current.min_fee_pct}
                        percentDisabled={minFixedOn}
                        onFixed={(value) => {
                          patchLeg(leg, 'min_fee_fixed', value);
                          if (value.trim() !== '') {
                            patchLeg(leg, 'min_fee_pct', '');
                          }
                        }}
                        onPercent={(value) => {
                          patchLeg(leg, 'min_fee_pct', value);
                          if (filledUnit(value)) {
                            patchLeg(leg, 'min_fee_fixed', '');
                          }
                        }}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label>{t('pages.admin.offers.fields.limits')}</Label>
                      <input
                        className="w-full rounded-xl border border-border/60 bg-background/60 px-3.5 py-2.5 text-sm focus:ring-2 focus:ring-indigo-500/40 focus:outline-none"
                        placeholder={t(
                          'pages.admin.offers.fields.limitsPlaceholder',
                        )}
                        value={current.limits}
                        onChange={(event) =>
                          patchLeg(leg, 'limits', event.target.value)
                        }
                      />
                    </div>
                    <div className="col-span-2 space-y-1.5">
                      <Label>{t('pages.admin.offers.fields.kycLimits')}</Label>
                      <input
                        className="w-full rounded-xl border border-border/60 bg-background/60 px-3.5 py-2.5 text-sm focus:ring-2 focus:ring-indigo-500/40 focus:outline-none"
                        placeholder={t(
                          'pages.admin.offers.fields.kycPlaceholder',
                        )}
                        value={current.kyc_limits}
                        onChange={(event) =>
                          patchLeg(leg, 'kyc_limits', event.target.value)
                        }
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

OfferPricingSection.displayName = 'OfferPricingSection';
